import React, { useState, useEffect } from 'react';
import { FaPencilAlt, FaSave, FaImage, FaTimes, FaPlus, FaLightbulb, FaTrash } from 'react-icons/fa';
import api from '../../services/apiService';
import AchievementCoin from '../AchievementCoin';
import ImageUploader from '../ImageUploader';
import { authService } from '../../services/authService';
import { getMatchReportAchievementGroups, getAchievementTitleById } from '../../data/achievements';
import '../../scss/match/matchReport.scss';

const PLAYER_ACHIEVEMENTS = getMatchReportAchievementGroups();

// Separate component for the example report content to avoid duplication
const ExampleReportContent = ({ teamName = 'Our Team', onUseExample, isAdmin }) => {
  return (
    <div className="example-report-preview">
      <div className="example-report-header">
        <h3>Example Match Report</h3>
        <p className="example-note">This is an example of a comprehensive match report.</p>
      </div>
      
      <div className="report-summary">
        An exciting 3-2 victory for {teamName} against The Rivals in challenging conditions, with some standout performances from our midfield and forwards.
      </div>
      
      <div className="report-content">
        <p>The team started strongly with high energy and good pressing in the first 15 minutes. We dominated possession early on, creating several chances before Alex opened the scoring with a well-placed finish after a brilliant through ball from Sam.</p>
        <p>Our opponents equalized against the run of play around the 25-minute mark, exploiting space on our left side. This briefly knocked our confidence, but the team regrouped well and retook the lead just before halftime with Jamie finishing off a fantastic team move.</p>
        <p>The second half started with increased pressure from the opposition, and they equalized again after a defensive mixup. Rather than dropping heads, the team showed great character to push for a winner. The decisive goal came in the 70th minute when Taylor's corner was headed home powerfully by Pat.</p>
        <p>Defensively, we looked much more organized in the final 20 minutes, showing good game management to see out the win.</p>
      </div>
      
      <div className="report-insights">
        <div className="report-positives">
          <h3>Positives</h3>
          <div className="insight-content">
            <p>• Excellent attacking movement and link-up play in the final third</p>
            <p>• Strong character shown to come back and win after being pegged back twice</p>
            <p>• Set piece execution was much improved from previous matches</p>
            <p>• Substitutes made a positive impact when introduced</p>
            <p>• Good communication between the defensive line in closing stages</p>
          </div>
        </div>
        
        <div className="report-improvements">
          <h3>Areas for Improvement</h3>
          <div className="insight-content">
            <p>• Need to maintain concentration after scoring, as both opposition goals came soon after our own</p>
            <p>• Left side vulnerability needs addressing in training</p>
            <p>• Decision-making in transition could be sharper</p>
            <p>• Need to be more clinical with chances - should have scored more from the opportunities created</p>
            <p>• Fitness levels need improvement as some players tired noticeably in the last 15 minutes</p>
          </div>
        </div>
      </div>
      
      <div className="player-mentions">
        <h3>Player Highlights</h3>
        <div className="mentions-list">
          <div className="player-mention">
            <div className="player-name">Alex Smith</div>
            <div className="player-comment">Excellent performance with a goal and an assist. Created numerous chances and worked tirelessly throughout.</div>
          </div>
          <div className="player-mention">
            <div className="player-name">Pat Johnson</div>
            <div className="player-comment">Commanding presence in defense and scored the crucial winning goal. Led by example as captain.</div>
          </div>
          <div className="player-mention">
            <div className="player-name">Jamie Williams</div>
            <div className="player-comment">Scored a well-taken goal and showed great technical ability in tight spaces. Growing in confidence each game.</div>
          </div>
        </div>
      </div>
      
      {isAdmin && onUseExample && (
        <div className="admin-action-section">
          <button 
            className="create-button" 
            onClick={onUseExample}
          >
            <FaLightbulb /> Use This Example
          </button>
        </div>
      )}
    </div>
  );
};

const MatchReport = ({ match_id, isAdmin }) => {
  const [report, setReport] = useState({
    title: '',
    summary: '',
    content: '',
    positives: '',
    improvements: '',
    player_mentions: [],
    published: false,
    images: [],
    coach_notes: ''
  });
  const [match, setMatch] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reportExists, setReportExists] = useState(false);
  const [playersList, setPlayersList] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  
  // Function to load team shields with fallback
  const getTeamShields = () => {
    // Import default logo
    const defaultLightLogo = require('../../images/teams/logo-light.png');
    
    // Get team IDs based on home/away status
    const isHome = match?.is_home === 1 || match?.is_home === '1';
    const ourTeamId = match?.team_a_id || match?.team_id || sessionStorage.getItem('selected_team_id');
    const oppositionTeamId = match?.opposition_team_id || match?.team_b_id;
    
    // Initialize with default paths
    let homeTeamShield = defaultLightLogo;
    let awayTeamShield = defaultLightLogo;
    
    // Set up shields based on home/away status
    if (isHome) {
      // We're playing at home - we're home team, opposition is away team
      if (ourTeamId) {
        homeTeamShield = `https://football.pebbledesigns.co.uk/api/images/teams/${ourTeamId}/logo-dark.png`;
      }
      if (oppositionTeamId) {
        awayTeamShield = `https://football.pebbledesigns.co.uk/api/images/teams/${oppositionTeamId}/logo-dark.png`;
      }
    } else {
      // We're playing away - opposition is home team, we're away team
      if (oppositionTeamId) {
        homeTeamShield = `https://football.pebbledesigns.co.uk/api/images/teams/${oppositionTeamId}/logo-dark.png`;
      }
      if (ourTeamId) {
        awayTeamShield = `https://football.pebbledesigns.co.uk/api/images/teams/${ourTeamId}/logo-dark.png`;
      }
    }
    
    return { homeTeamShield, awayTeamShield };
  };
  
  useEffect(() => {
    fetchMatchReport();
  }, [match_id]);
  
  // Fetch players list for the dropdown when entering edit mode
  useEffect(() => {
    if (isEditing) {
      fetchPlayers();
    }
  }, [isEditing]);
  
  const fetchPlayers = async () => {
    try {
      setLoadingPlayers(true);
      // Get the team_id from sessionStorage
      const teamId = sessionStorage.getItem('selected_team_id');
      
      if (!teamId) {
        console.error('No team ID found in session storage');
        return;
      }
      
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_players.php?team_id=${teamId}`);
      
      if (response.data.success) {
        setPlayersList(response.data.players);
      } else {
        console.error('Error fetching players:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoadingPlayers(false);
    }
  };
  
  const fetchMatchReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_match_report.php?match_id=${match_id}`);
      const data = response.data;
      
      if (data.success && data.report) {
        // Ensure player_mentions is always an array
        const reportData = {
          ...data.report,
          player_mentions: Array.isArray(data.report.player_mentions) ? data.report.player_mentions : [],
          images: Array.isArray(data.report.images) ? data.report.images : [],
          coach_notes: data.report.coach_notes || ''
        };
        setReport(reportData);
        setMatch(data.match);
        setReportExists(true);
      } else {
        // Initialize with default values if no report exists
        const defaultTitle = sessionStorage.getItem('selected_team_name') || 'Our Team';
        setReport({
          title: `${defaultTitle} Match Report`,
          summary: '',
          content: '',
          positives: '',
          improvements: '',
          player_mentions: [],
          published: false,
          images: [],
          coach_notes: ''
        });
        setIsEditing(isAdmin); // Auto enter edit mode for admin if no report exists
      }
    } catch (error) {
      console.error('Error fetching match report:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      const reportData = {
        ...report,
        published: true
      };

      const response = await api.post(`${process.env.REACT_APP_API_URL}/save_match_report.php`, {
        match_id: match_id,
        report: reportData
      });
      
      const data = response.data;
      if (data.success) {
        setReportExists(true);
        setIsEditing(false);
        // Reload report to ensure we have the latest version
        fetchMatchReport();
      } else {
        alert('Failed to save report: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving match report:', error);
      alert('An error occurred while saving the report');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePhoto = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const response = await api.post('/delete_image.php', { image_url: imageUrl });

      if (response.data.success) {
        setReport(prev => ({
          ...prev,
          images: prev.images.filter(img => img !== imageUrl)
        }));
      } else {
        alert('Failed to delete photo: ' + response.data.message);
      }
    } catch (error) {
      alert('An error occurred while deleting the photo.');
    }
  };

  const loadExampleContent = () => {
    const teamName = sessionStorage.getItem('selected_team_name') || 'Our Team';
    const opponent = 'The Rivals';
    
    setReport({
      title: `${teamName} vs ${opponent} - Match Report`,
      summary: `An exciting 3-2 victory for ${teamName} against ${opponent} in challenging conditions, with some standout performances from our midfield and forwards.`,
      content: `The team started strongly with high energy and good pressing in the first 15 minutes. We dominated possession early on, creating several chances before Alex opened the scoring with a well-placed finish after a brilliant through ball from Sam.\n\nOur opponents equalized against the run of play around the 25-minute mark, exploiting space on our left side. This briefly knocked our confidence, but the team regrouped well and retook the lead just before halftime with Jamie finishing off a fantastic team move.\n\nThe second half started with increased pressure from the opposition, and they equalized again after a defensive mixup. Rather than dropping heads, the team showed great character to push for a winner. The decisive goal came in the 70th minute when Taylor's corner was headed home powerfully by Pat.\n\nDefensively, we looked much more organized in the final 20 minutes, showing good game management to see out the win.`,
      positives: `• Excellent attacking movement and link-up play in the final third\n• Strong character shown to come back and win after being pegged back twice\n• Set piece execution was much improved from previous matches\n• Substitutes made a positive impact when introduced\n• Good communication between the defensive line in closing stages`,
      improvements: `• Need to maintain concentration after scoring, as both opposition goals came soon after our own\n• Left side vulnerability needs addressing in training\n• Decision-making in transition could be sharper\n• Need to be more clinical with chances - should have scored more from the opportunities created\n• Fitness levels need improvement as some players tired noticeably in the last 15 minutes`,
      player_mentions: [
        {
          id: '1',
          player_name: 'Alex Smith',
          achievement: 'goal-machine',
          comment: 'Excellent performance with a goal and an assist. Created numerous chances and worked tirelessly throughout.'
        },
        {
          id: '2',
          player_name: 'Pat Johnson',
          achievement: 'captains-mindset',
          comment: 'Commanding presence in defense and scored the crucial winning goal. Led by example as captain.'
        },
        {
          id: '3',
          player_name: 'Jamie Williams',
          achievement: 'magic-moment',
          comment: 'Scored a well-taken goal and showed great technical ability in tight spaces. Growing in confidence each game.'
        }
      ],
      published: false,
      coach_notes: ''
    });
  };
  
  const handleAddPlayerMention = () => {
    setReport(prev => ({
      ...prev,
      player_mentions: [
        ...(prev.player_mentions || []), 
        { 
          id: Date.now().toString(),
          player_id: '', // Now we'll store player_id instead of player_name
          player_name: '', // We'll still keep this for display purposes
          comment: '',
          achievement: '' // Add achievement field
        }
      ]
    }));
  };
  
  const handlePlayerMentionChange = (id, field, value) => {
    setReport(prev => ({
      ...prev,
      player_mentions: (prev.player_mentions || []).map(mention => {
        if (mention.id === id) {
          if (field === 'player_id') {
            // Find the player's name from the selected player_id
            const selectedPlayer = playersList.find(player => player.player_id === value);
            if (selectedPlayer) {
              return { 
                ...mention, 
                player_id: value,
                player_name: `${selectedPlayer.first_name} ${selectedPlayer.last_name}`
              };
            }
          }
          return { ...mention, [field]: value };
        }
        return mention;
      })
    }));
  };
  
  const handleRemovePlayerMention = (id) => {
    setReport(prev => ({
      ...prev,
      player_mentions: (prev.player_mentions || []).filter(mention => mention.id !== id)
    }));
  };

  if (loading) {
    return <div className="match-report-loading">Loading match report...</div>;
  }
  
  if (isEditing) {
    // EDIT MODE
    return (
      <div className="match-report match-report-edit">
        <div className="editor-header">
          <h2>Edit Match Report</h2>
        </div>
        
        <div className="report-form">
          <h3 className="section-title">Match Report (Visible to All)</h3>
          <div className="form-section">
            <div className="form-group">
              <label>Report Title</label>
              <input 
                type="text"
                value={report.title} 
                onChange={(e) => setReport(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., 'Thrilling 3-2 Victory Against The Rivals'"
                className="title-input"
              />
              <small className="field-hint">
                💡 Tip: Use an engaging headline like "{sessionStorage.getItem('selected_team_name') || 'Your Team'} Continue Winning Ways" or "Thrilling 3-2 Victory Against The Rivals"
              </small>
            </div>
          </div>
          
          <div className="form-section">
            <div className="form-group">
              <label>Match Report</label>
              <textarea 
                value={report.content} 
                onChange={(e) => setReport(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write a detailed report about the match"
                rows={11}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group half">
              <label>Positives</label>
              <textarea 
                value={report.positives} 
                onChange={(e) => setReport(prev => ({ ...prev, positives: e.target.value }))}
                placeholder="What went well in this match?"
                rows={5}
              />
            </div>
            
            <div className="form-group half">
              <label>Areas for Improvement</label>
              <textarea 
                value={report.improvements} 
                onChange={(e) => setReport(prev => ({ ...prev, improvements: e.target.value }))}
                placeholder="What could be improved for next time?"
                rows={5}
              />
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label>Match Photos</label>
              <ImageUploader 
                matchId={match_id}
                teamId={sessionStorage.getItem('selected_team_id')}
                onUploadSuccess={(url) => {
                  // Here you would typically update the report state with the new image URL
                  console.log('Uploaded image URL:', url);
                }}
              />
            </div>

            {report.images && report.images.length > 0 && (
              <div className="form-group">
                <label>Uploaded Photos</label>
                <div className="uploaded-photos-grid">
                  {report.images.map((image, index) => (
                    <div key={index} className="photo-preview-item">
                      <img src={`${process.env.REACT_APP_API_URL}/${image}`} alt={`Uploaded ${index + 1}`} />
                      <button 
                        className="delete-photo-btn"
                        onClick={() => handleDeletePhoto(image)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-group">
              <div className="section-header">
                <label>Player Achievements</label>
                <button 
                  className="add-button" 
                  onClick={handleAddPlayerMention}
                  type="button"
                >
                  <FaPlus /> Choose Player
                </button>
              </div>
              
              {loadingPlayers ? (
                <div className="loading-message">Loading players...</div>
              ) : (
                report.player_mentions.map((mention, index) => (
                  <div className="player-mention-row" key={mention.id}>
                    <select
                      value={mention.player_id}
                      onChange={(e) => handlePlayerMentionChange(mention.id, 'player_id', e.target.value)}
                      className="player-name-input"
                      disabled={loadingPlayers}
                    >
                      <option value="">Select a player</option>
                      {playersList.map(player => (
                        <option 
                          key={player.player_id} 
                          value={player.player_id}
                        >
                          {player.first_name} {player.last_name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={mention.achievement || ''}
                      onChange={(e) => handlePlayerMentionChange(mention.id, 'achievement', e.target.value)}
                      className="player-achievement-input"
                    >
                      <option value="">Select an achievement (optional)</option>
                      {Object.entries(PLAYER_ACHIEVEMENTS).map(([category, awards]) => (
                        <optgroup key={category} label={category}>
                          {awards.map(award => (
                            <option key={award.id} value={award.id}>
                              {award.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <textarea 
                      value={mention.comment} 
                      onChange={(e) => handlePlayerMentionChange(mention.id, 'comment', e.target.value)}
                      placeholder="Comment about the player's performance"
                      className="player-comment-input"
                      rows={3}
                    />
                    <button 
                      className="remove-button" 
                      onClick={() => handleRemovePlayerMention(mention.id)}
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div className="form-section coaches-section">
            <h3 className="section-title">Your Match Notes (Coaches Only)</h3>
            <div className="form-group">
              <p className="field-intro">
                Use this space for your private notes about the game. Mention player performance, areas for improvement, and any opposition information that might be useful for future matches.
              </p>
              <textarea 
                value={report.coach_notes} 
                onChange={(e) => setReport(prev => ({ ...prev, coach_notes: e.target.value }))}
                placeholder="Add your private notes here..."
                rows={8}
              />
            </div>
          </div>

          <div className="form-buttons">
            <button 
              className="save-button" 
              onClick={handleSave}
              disabled={saving}
              type="button"
            >
              {saving ? 'Saving...' : <>
                <FaSave /> Publish Report
              </>}
            </button>
            <button 
              className="cancel-button" 
              onClick={() => {
                if (reportExists) {
                  setIsEditing(false);
                  fetchMatchReport();
                } else {
                  window.history.back();
                }
              }}
              disabled={saving}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Use the saved title or generate a fallback headline
  const getHeadline = () => {
    if (report.title && report.title.trim()) {
      return report.title;
    }
    
    // Fallback to generated headline if no title is saved
    const teamName = sessionStorage.getItem('selected_team_name') || 'Our Team';
    const headlines = [
      `${teamName} Dominate in Thrilling Victory`,
      `Spectacular Performance from ${teamName}`,
      `${teamName} Show Class in Latest Outing`,
      `Impressive Display Seals Victory for ${teamName}`,
      `${teamName} Continue Winning Ways`,
      `Outstanding Team Performance from ${teamName}`,
      `${teamName} Deliver When It Matters`,
      `Clinical Finish Crowns ${teamName} Victory`
    ];
    return headlines[Math.floor(Math.random() * headlines.length)];
  };

  const getFormattedMatchDate = () => {
    if (!match || !match.match_date) return 'Match Date TBC';
    
    const matchDate = new Date(match.match_date + (match.match_time ? ` ${match.match_time}` : ''));
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    if (match.match_time) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    
    return matchDate.toLocaleDateString('en-GB', options);
  };

  // VIEW MODE
  // Check if user is a parent or player (not admin/coach/manager)
  const isParentOrPlayer = !isAdmin && !authService.isManager();
  
  return (
    <div className={`match-report match-report-view ${isAdmin ? 'admin-view' : ''}`}>
      {/* Admin edit button - only show if report is published or admin is editing */}
      {isAdmin && report.published && (
        <button 
          className="edit-button" 
          onClick={() => setIsEditing(true)}
        >
          <FaPencilAlt /> Edit Report
        </button>
      )}
      
      {/* Show different content based on report existence and publication status */}
      {!reportExists ? (
        // For parents and players, show a message instead of example content
        isParentOrPlayer ? (
          <div className="match-report-coming-soon">
            <div className="coming-soon-content">
              <h2>Match Report Coming Soon</h2>
              <p>The match report is being prepared and will be available soon.</p>
            </div>
          </div>
        ) : (
          <ExampleReportContent 
            teamName={sessionStorage.getItem('selected_team_name') || 'Our Team'} 
            isAdmin={isAdmin}
            onUseExample={isAdmin ? () => {
              loadExampleContent();
              setIsEditing(true);
            } : null}
          />
        )
      ) : !report.published ? (
        // Report exists but not published
        <div className="match-report-coming-soon">
          <div className="coming-soon-content">
            <h2>Match Report Coming Soon</h2>
            <p>The match report is being prepared and will be available soon.</p>
            {isAdmin && (
              <button 
                className="edit-button" 
                onClick={() => setIsEditing(true)}
              >
                <FaPencilAlt /> Edit Match Report
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="newspaper-layout">
          {/* Newspaper Header */}
          <header className="newspaper-header">
            <div className="newspaper-masthead">
              <div className="publication-name">MATCH REPORT</div>
            </div>
            <h1 className="main-headline">{getHeadline()}</h1>
            <div className="match-details">
              <div className="team-section home-team">
                <div className="team-shield">
                  <img 
                    src={getTeamShields().homeTeamShield} 
                    alt="Home team shield"
                    onError={(e) => {
                      e.target.src = require('../../images/teams/logo-light.png');
                    }}
                  />
                </div>
                <span className="team-name">
                  {match?.is_home === 1 || match?.is_home === '1' 
                    ? (sessionStorage.getItem('selected_team_name') || 'Our Team')
                    : (match?.opposition || 'Opposition')
                  }
                </span>
              </div>
              <div className="vs-section">
                <span className="vs-text">vs</span>
              </div>
              <div className="team-section away-team">
                <div className="team-shield">
                  <img 
                    src={getTeamShields().awayTeamShield} 
                    alt="Away team shield"
                    onError={(e) => {
                      e.target.src = require('../../images/teams/logo-light.png');
                    }}
                  />
                </div>
                <span className="team-name">
                  {match?.is_home === 1 || match?.is_home === '1'
                    ? (match?.opposition || 'Opposition')
                    : (sessionStorage.getItem('selected_team_name') || 'Our Team')
                  }
                </span>
              </div>
            </div>
            <div className="publish-date">
              <span className="published-label">Published</span>
              <span className="match-date">{getFormattedMatchDate()}</span>
            </div>
          </header>

          {/* Hero Image Masthead */}
          <section className="hero-masthead">
            <div className="hero-image">
              <img 
                src={report.images && report.images.length > 0 
                  ? `${process.env.REACT_APP_API_URL}/${report.images[0]}`
                  : "https://media.istockphoto.com/id/1620960870/photo/happy-teenage-female-soccer-team-goal-celebration-run-and-congratulations-cheer.jpg?s=612x612&w=0&k=20&c=rPZ-6uJzgoxaiFTFloRoyUGoOP31On-i98jXdARwahk="
                }
                alt="Match highlight"
                className="hero-photo"
              />
            </div>
            {report.images && report.images.length > 1 && (
              <div className="image-carousel">
                <div className="carousel-container">
                  {report.images.slice(1).map((image, index) => (
                    <div key={index} className="carousel-item">
                      <img 
                        src={`${process.env.REACT_APP_API_URL}${image}`}
                        alt={`Match highlight ${index + 1}`}
                        className="carousel-photo"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Main Content */}
          <div className="newspaper-content">
            {/* Lead Story */}
            <section className="lead-story">
              {report.summary && report.summary.trim() && (
                <div className="story-hook">
                  {report.summary}
                </div>
              )}
              
              <div className="story-content">
                {(report.content).split('\n').map((paragraph, index) => (
                  paragraph ? <p key={index}>{paragraph}</p> : null
                ))}
              </div>
            </section>

            {/* Analysis Section */}
            <section className="match-analysis">
              <h3>Match Analysis</h3>
              <div className="analysis-grid">
                <div className="analysis-positives">
                  <h4>Key Strengths</h4>
                  <div className="analysis-content">
                    {(report.positives).split('\n').map((point, index) => (
                      point ? <p key={index}>• {point}</p> : null
                    ))}
                  </div>
                </div>
                
                <div className="analysis-improvements">
                  <h4>Development Areas</h4>
                  <div className="analysis-content">
                    {(report.improvements).split('\n').map((point, index) => (
                      point ? <p key={index}>• {point}</p> : null
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {isAdmin && report.coach_notes && (
              <section className="coach-notes">
                <h3>Your Match Notes</h3>
                <div className="notes-content">
                  {report.coach_notes.split('\n').map((paragraph, index) => (
                    paragraph ? <p key={index}>{paragraph}</p> : null
                  ))}
                </div>
              </section>
            )}

            {/* Player Highlights */}
            {report.player_mentions && report.player_mentions.length > 0 && (
                          <section className="player-highlights">
              <h3>Player Highlights</h3>
              <div className="highlights-three-column">
                {report.player_mentions.map((mention, index) => (
                  <div className="highlight-item" key={mention.id || index}>
                    {mention.achievement ? (
                      <div className="highlight-with-award">
                        <div className="coin-container">
                          <AchievementCoin 
                            achievementId={mention.achievement} 
                            size="medium" 
                            className="highlight-coin"
                          />
                        </div>
                        <div className="highlight-info">
                          <div className="player-name">{mention.player_name}</div>
                          <div className="achievement-title">
                            {getAchievementTitleById(mention.achievement)}
                          </div>
                        </div>
                        <div className="highlight-comment">{mention.comment}</div>
                      </div>
                    ) : (
                      <div className="highlight-text-only">
                        <div className="player-name">{mention.player_name}</div>
                        <div className="highlight-comment">{mention.comment}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchReport; 